/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyResponseType } from '@services';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

interface PriceStripProps {
  property: PropertyResponseType;
}

interface PriceItem {
  label: string;
  value: string;
  /** Sale and rent carry the brand colour; the rest stay in dark ink. */
  headline?: boolean;
}

/**
 * Formats a numeric value as a Brazilian Real currency string, or an em dash when absent.
 */
function formatCurrency(value?: number): string {
  if (value == null || value === 0) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

/**
 * Returns the first IPTU entry, or an em dash when the list is empty.
 */
function formatIptu(iptu?: number | string[]): string {
  if (!iptu) return '—';
  if (Array.isArray(iptu)) return iptu[0] || '—';
  return formatCurrency(iptu);
}

/**
 * Price per square metre, derived when the record does not carry it.
 *
 * The backend computes `price_per_sqm` on write, so a listing that has not
 * been edited since it was imported simply has none — which is most of an
 * imported portfolio. The division is the same one the backend performs,
 * and both operands are already on the record, so deriving it here shows
 * the figure instead of hiding the row.
 */
function resolvePricePerSqm(property: PropertyResponseType): number | undefined {
  const stored = property.pricing?.sale?.price_per_sqm;
  if (stored) return stored;
  const price = property.pricing?.sale?.price;
  const area = property.characteristics?.total_area;
  if (!price || !area) return undefined;
  return price / Number(area);
}

/**
 * Price over the built area, when that is a different number from the total.
 *
 * The two together separate land from building: on a large lot with a small
 * house they diverge sharply, and the pair is what tells a broker which of
 * the two the asking price is really about. When the areas match — a flat,
 * usually — the second figure would repeat the first, so it is left out
 * rather than filling a column with the same number.
 */
function resolvePricePerBuiltSqm(
  property: PropertyResponseType,
  pricePerSqm: number | undefined,
): number | undefined {
  const price = property.pricing?.sale?.price;
  const built = property.characteristics?.constructed_area;
  if (!price || !built) return undefined;
  const value = price / Number(built);
  if (!pricePerSqm) return value;
  // Within a rounded currency unit of each other reads as the same figure.
  return Math.round(value) === Math.round(pricePerSqm) ? undefined : value;
}

/**
 * Collects the monetary facts worth stating above the tabs.
 *
 * Each fact is described once as a {@link PriceItemSpec}; a spec contributes an
 * item only when its `value` is non-null, so the sequence of conditions lives
 * in the specs (each a single expression) rather than as branches here.
 */
function collectItems(property: PropertyResponseType, t: (k: string) => string): PriceItem[] {
  const pricing = property.pricing ?? {};
  const pricePerSqm = resolvePricePerSqm(property);

  const specs: PriceItemSpec[] = [
    { labelKey: 'detailView.priceStrip.salePrice', value: pricing.sale?.price, headline: true },
    { labelKey: 'detailView.priceStrip.rentalPrice', value: pricing.rental?.price, headline: true },
    {
      labelKey: 'detailView.priceStrip.shortTermPrice',
      text: perNight(pricing.short_term?.price, t),
      headline: true,
    },
    { labelKey: 'detailView.priceStrip.firstAuction', value: pricing.auction?.first_price, headline: true },
    { labelKey: 'detailView.priceStrip.pricePerM2', value: pricePerSqm },
    { labelKey: 'detailView.priceStrip.pricePerBuiltM2', value: resolvePricePerBuiltSqm(property, pricePerSqm) },
    { labelKey: 'detailView.priceStrip.condominium', value: property.condominium?.monthly_fee },
    { labelKey: 'detailView.priceStrip.iptu', text: nonEmptyDash(formatIptu(pricing.taxes?.iptu_annual)) },
    { labelKey: 'detailView.priceStrip.acceptsExchange', text: exchangeText(pricing.sale?.accepts_exchange, t) },
  ];

  return specs.map((spec) => specToItem(spec, t)).filter((item): item is PriceItem => item !== null);
}

/**
 * Describes one candidate price fact: either a numeric `value` (currency-
 * formatted, shown only when truthy) or a pre-rendered `text` (string or null).
 */
interface PriceItemSpec {
  labelKey: string;
  value?: number | null;
  text?: string | null;
  headline?: boolean;
}

/** Returns the string unless it is the em-dash placeholder, in which case null. */
/**
 * The Temporada price with its per-night suffix ("R$ 5.100/diária").
 *
 * @param price - `pricing.short_term.price`.
 * @param t - Translator.
 * @returns The text, or null when there is no price.
 */
function perNight(price: number | null | undefined, t: (k: string) => string): string | null {
  if (!price) return null;
  return `${formatCurrency(price)}${t('detailView.priceStrip.perNight')}`;
}

/** Null for the em-dash placeholder, otherwise the value unchanged. */
function nonEmptyDash(value: string): string | null {
  return value === '—' ? null : value;
}

/** The "accepts exchange" cell text, or null when the listing does not. */
function exchangeText(accepts: boolean | undefined, t: (k: string) => string): string | null {
  return accepts ? t('detailView.terms.yes') : null;
}

/**
 * Turns a spec into a {@link PriceItem}, or null when it has nothing to show.
 *
 * A numeric `value` renders only when truthy (0 and null both omit the fact,
 * matching the old per-figure truthy guards); a `text` spec renders whenever
 * it is a non-null string.
 */
function specToItem(spec: PriceItemSpec, t: (k: string) => string): PriceItem | null {
  const value = spec.text ?? (spec.value ? formatCurrency(spec.value) : null);
  if (value === null) return null;
  return { label: t(spec.labelKey), value, headline: spec.headline };
}

/**
 * The listing's money, in one band above the tabs.
 *
 * Sale and rent are the two figures a negotiation turns on, so they carry the
 * brand colour and the larger size; price per m², condominium, IPTU and
 * exchange are context and stay in dark ink. If every figure were brand
 * colour, none of them would read as the important one.
 *
 * @param props.property - The loaded property.
 * @returns The rendered strip, or null when the listing has no prices.
 */
export const PriceStrip: React.FC<PriceStripProps> = ({ property }) => {
  const { t } = useTranslation('features/properties');
  const items = collectItems(property, t);

  if (items.length === 0) return null;

  return (
    <div
      data-testid="property-price-strip"
      className="flex flex-wrap items-end gap-[var(--space-5)] rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-card)] px-[var(--space-4)] py-[var(--space-3-5)]"
    >
      {items.map((item) => (
        <div key={item.label} className="flex min-w-0 flex-col gap-[var(--space-0-5)]">
          <Text
            component="span"
            className="whitespace-nowrap text-[length:var(--text-meta)] [font-weight:var(--font-bold)] text-[var(--text-tertiary)]"
          >
            {item.label}
          </Text>
          <Text
            component="span"
            className={cn(
              'whitespace-nowrap tabular-nums leading-[var(--leading-tight)] tracking-[var(--tracking-tight)]',
              item.headline
                ? 'text-[length:var(--text-2xl)] [font-weight:var(--font-bold)] text-[var(--seed-base)]'
                : 'text-[length:var(--text-body)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]',
            )}
          >
            {item.value}
          </Text>
        </div>
      ))}
    </div>
  );
};
